import React, {useEffect, useRef, useState} from 'react';
import { View, PanResponder, TouchableOpacity, useWindowDimensions } from 'react-native';
import Svg, { G, Text } from 'react-native-svg';
import { BarChart, Grid, XAxis, YAxis } from 'react-native-svg-charts';
import * as scale from 'd3-scale';
import _ from 'lodash';
import Util from './Util';
import {BasicText, FlexBlock, StrongText} from '../Styles'
import styled from 'styled-components/native'
import { useLayoutEffect } from 'react';
import { isMobile } from '../WasteApp';

//https://github.com/JesperLekland/react-native-svg-charts#barchart

const CustomBarChart = ({
    /*bar chart props*/ data, height = 300, horizontal = false, svg = {}, contentInset = { top: 10, left: 0, right: 0, bottom: 0 }, spacingInner = 0.05, spacingOuter = 0.05, showLabels = false, showGrid = true, showLegend = true,
    /*common chart props*/ yAccessor = ({ item, index }) => item, xAccessor = ({ item, index }) => index, yMin = undefined, yMax = undefined, xMin = undefined, xMax = undefined,
    /*children props*/ bandwidth = undefined, legendChildren = undefined,
    /*x axis*/ xAxisAccessor = undefined, xAxisData = undefined, xAxisInset = { left: 5, right: 0 }, xAxisScale = undefined, xAxisFormatLabel = (value, index) =>{ return index; }, xAxisSVGProperties = { fontSize: 14, fill: 'grey', fontFamily: 'Red Hat Display' }, xAxisLabel= undefined,
    /*y axis*/ yAxisWidth = 0, yAxisAccessor = undefined, yAxisData = undefined, yAxisInset = { top: 10, bottom: 15 }, yAxisScale = undefined, yAxisFormatLabel = (value, index) =>{ return value; }, yAxisSVGProperties = { fontSize: 14, fill: 'grey', fontFamily: 'Red Hat Display' }, yAxisLabel = undefined,
    extraKids = null, customMouseEvent = undefined, defaultDataPoint = undefined
    }) => {
    
    const yAxisRef = useRef(null);
    const xAxisRef = useRef(null);
    const [mousePosition, setMousePosition] = useState([-1, -1]);
    const [currentDataPoint, setCurrentDataPoint] = useState(null);
    const [chartData, setChartData] = useState(data);
    const [customXAxis, setCustomXAxis] = useState(null);
    const windowDimensions = useWindowDimensions();
    const [yWidth, setYWidth] = useState(yAxisWidth);
    const yAxisLabelRef = useRef(null)

    useEffect(()=>{
        if(defaultDataPoint != undefined){
            setCurrentDataPoint(defaultDataPoint);
        }
    }, [defaultDataPoint]);

    useEffect(() => {
        const newData = [];

        _.forEach(data, (d) => {
            let newD = null;
            if (typeof (d) == "object") {
                newD = _.cloneDeep(d);

                if (newD.data != undefined) {
                    const dData = [];

                    _.forEach(newD.data, (dd) => {
                        if (currentDataPoint != undefined && dd.label == currentDataPoint.label) {
                            if (dd.svg == undefined) {
                                dd.svg = {};
                            }
                            dd.svg.fillOpacity = 0.6;
                            dd.svg.stroke = "rgb(0, 74, 105)";
                        }

                        dData.push(dd);
                    });

                    newD.data = dData;
                } else {
                    if (currentDataPoint != undefined && newD.label == currentDataPoint.label) {
                        if (newD.svg == undefined) {
                            newD.svg = {};
                        }
                        newD.svg.fillOpacity = 0.6;
                        newD.svg.stroke = "rgb(0, 74, 105)";
                    }
                }
            } else {
                newD = d + 0;
            }

            newData.push(newD);
        });

        setChartData(newData);


    }, [data, currentDataPoint]);
    

    const CrazyMouse = (e)=>{
        let position = undefined;
        if(Util.isMobile()){
            position = {left: e.nativeEvent.locationX, top: e.nativeEvent.locationY};
        } else {
            const rect = e.currentTarget.getBoundingClientRect();
            position = {left: Math.floor(e.clientX - rect.left), top: Math.floor(e.clientY - rect.top) };
        }
        
		setMousePosition([position.left, position.top]);
	};

    /*style adjustments*/
    const boxPadding = 10; //boxPadding seemed like a mystery number that CrazyMouse and ChartDetails need
    let gridDirection = undefined;
    let graphPadding = 20;
    let xAxisTop = 15;
    let xAxisHeight = 30;

    let xAxisMobileStyle = {};

	if(Util.isMobile()){
        graphPadding = 5;
        xAxisMobileStyle = {rotation: 45, translateY: 0};
		xAxisTop = 0;
		xAxisHeight = 50;

        yAxisSVGProperties.fontSize = 12;
        xAxisSVGProperties.fontSize = 12;
	}

    xAxisSVGProperties = {...xAxisSVGProperties, ...xAxisMobileStyle};

    if(xAxisScale == undefined){
        xAxisScale = scale.scaleBand;
    }

    if(yAxisScale == undefined){
        yAxisScale = scale.scaleLinear;
    }

    if(horizontal == true){
        gridDirection = Grid.Direction.VERTICAL;
    } else {
        gridDirection = Grid.Direction.HORIZONTAL;
    }
        
    const doScaling = (xIndex, yVal, xFnc, yFnc, barBandwidth) => {
        const scaledX = xFnc(xIndex) + (barBandwidth / 2);
        let scaledY = yFnc(yVal);

        if (yVal > 0) {
            if (yVal > yMax) {
                scaledY -= 10;
            } else {
                scaledY += 15;
            }
        } else {
            if (yVal < yMin) {
                scaledY += 10;
            } else {
                scaledY -= 15;
            }
        }

        return [scaledX, scaledY];
    }

    const Labels = ({ x = undefined, y = undefined, bandwidth = 0, data = [] }) => {
        const stuff =  _.map(data, (value, index) => {
            let yValue = value;

            if (value.data != undefined) { //we have multiple data series being plotted

                if (value.data.length > 0) {
                    return _.map(value.data, (vData, vDataIndex) => {
                        if (typeof (vData) == "number") {
                            yValue = vData;
                        } else if (typeof (vData) == "object") {
                            yValue = vData.value;
                        }

                        const scales = doScaling(vDataIndex, yValue, x, y, bandwidth);

                        return <Text
                            key={index}
                            x={scales[0]}
                            y={scales[1]}
                            fontSize={14}
                            fill={'black'}
                            alignmentBaseline={'middle'}
                            textAnchor={'middle'}
                        >
                            {Util.round(yValue, 2)}
                        </Text>;
                    });

                }

                return null;
            } else { //singular series
                if (typeof (yValue) == "object") {
                    yValue = yValue.value;
                }
                const scales = doScaling(index, yValue, x, y, bandwidth);

                return <Text
                    key={index}
                    x={scales[0]}
                    y={scales[1]}
                    fontSize={14}
                    fill={'black'}
                    alignmentBaseline={'middle'}
                    textAnchor={'middle'}
                >
                    {Util.round(yValue, 2)}
                </Text>;
            }
        });

        //debugger;

        return <G>{stuff}</G>;
    };


    if(xAxisData == undefined || yAxisData == undefined){
        let tempData = [];

        if(chartData != undefined && chartData.length > 0){
            if(typeof(chartData[0]) == "object" && chartData[0].data != undefined){
                tempData = _.flatten(_.map(chartData, "data"));
            } else {
                tempData = chartData;
            }
        } else {
            tempData = [];
        }

        if(xAxisData == undefined){
            xAxisData = tempData;
        }

        if(yAxisData == undefined){
            yAxisData = tempData;
        }
    }

    if(yMin == undefined || yMax == undefined){
        //assign by data
        let tempData = [];
        if(typeof(chartData[0]) == "object" && chartData[0].data != undefined){
            tempData = _.flatten(_.map(chartData, "data"));
        } else {
            tempData = chartData;
        }

        if(tempData.length > 0){
            if(horizontal == true){
                if(xMin == undefined){
                    xMin = _.minBy(tempData, "value")["value"];

                    if(xMin > 0){
                        xMin = 0;
                    }
                }

                if(xMax == undefined){
                    xMax = _.maxBy(tempData, "value")["value"];
                }
            } else {
                if(yMin == undefined){
                    yMin = _.minBy(tempData, "value")["value"];

                    if(yMin > 0){
                        yMin = 0;
                    }
                }

                if(yMax == undefined){
                    yMax = _.maxBy(tempData, "value")["value"];
                    const maxDataByValue2 = _.maxBy(tempData, "value2");

                    if(maxDataByValue2 != undefined){
                        const value2Max = maxDataByValue2["value2"];
                        if (value2Max > yMax && extraKids != null){
                            yMax = value2Max;
                        }
                    }                    
                }
            }
        }
    }
    
    if(xAxisAccessor == undefined){
        xAxisAccessor = xAccessor;
    }

    if(yAxisAccessor == undefined){
        yAxisAccessor = yAccessor;
    }

    const extraProps = {};

    if( !Util.isMobile() ){
        extraProps.onMouseMove = (e)=>CrazyMouse(e);
    }

    const xAxisStyle = {height: xAxisHeight, top: xAxisTop};
    const yAxisStyle = {height: height - (graphPadding * 2) + yAxisInset.bottom, minWidth: yWidth};
    const style = {height: height, width: Util.isMobile()? "95%" : "100%"};
    const yWidthWithLabel = (yWidth != undefined? yWidth : 0) + (yAxisLabel != null? 25 : 0) + 10;
    const chartMarginLeft = 10;
    
    return <View style={{minWidth: "100%"}}>
        {showLegend == true ? <CrazyLegend currentDataPoint={currentDataPoint} data={chartData} legendChildren={legendChildren} /> : null}
        <View style={{width: "100%"}}>
            <TouchableOpacity style={{ height: style.height, padding: graphPadding, flexDirection: 'row' }} onPress={(e) => CrazyMouse(e)} activeOpacity={1} {...extraProps}>
                <View style={{flexDirection: "row", width: yWidthWithLabel}}>
                    {yAxisLabel != null? <View style={{width: 25, justifyContent: "center", alignItems: "center"}}>
						<Svg><Text rotation={270} translateX={Util.isMobile()? 15 : 150} translateY={Util.isMobile()? 180 : 75} text-anchor="middle" fontSize={12} fontFamily="Red Hat Display" fill="grey" fontWeight={500} letterSpacing={0.5}>{yAxisLabel}</Text></Svg>
					</View> : null}
                    <YAxis
                        ref={(ref)=>{ 
                            yAxisRef.current = ref;
                            if(yAxisRef.current != null && yAxisRef.current.state != null && yAxisRef.current.state.width != yWidth){
                                setYWidth(yAxisRef.current.state.width); //+10
                            }
                        }}
                        data={yAxisData}
                        yAccessor={yAxisAccessor}
                        contentInset={yAxisInset}
                        style={yAxisStyle}
                        formatLabel={yAxisFormatLabel}
                        svg={yAxisSVGProperties}
                        spacingInner={spacingInner}
                        spacingOuter={spacingOuter}
                        scale={yAxisScale}
                        min={yMin}

                    />
                </View>
                <View style={{ flex: 1, marginLeft: chartMarginLeft, flexDirection: "column"}}>
                    <BarChart
                        style={{ ...{ flex: 1 }, ...style }}
                        data={chartData}
                        svg={svg}
                        contentInset={contentInset}
                        horizontal={horizontal}
                        spacingInner={spacingInner}
                        spacingOuter={spacingOuter}
                        yAccessor={yAccessor}
                        xAccessor={xAccessor}
                        height={height}
                        gridMin={0}
                        yMax={yMax}
                    >
                        {showGrid == true ? <Grid direction={gridDirection} /> : null}
                        {showLabels == true ? <Labels /> : null}
                        <MouseEvent isMobile={Util.isMobile()} data={chartData} customMouseEvent={customMouseEvent} horizontal={horizontal} yWidth={yWidthWithLabel} boxPadding={boxPadding} spacingInner={spacingInner} spacingOuter={spacingOuter} bandwidth={bandwidth} mousePosition={mousePosition} padding={graphPadding} setCurrentDataPoint={(dp) => { _.isEqual(currentDataPoint, dp) ? null : setCurrentDataPoint(dp) }} />
                        <ChartDetails chartMarginLeft={chartMarginLeft} isMobile={Util.isMobile()} yWidth={yWidthWithLabel} formatLabel={xAxisFormatLabel} accessor={horizontal == true? yAccessor : xAccessor} horizontal={horizontal} style={style} setCustomXAxis={setCustomXAxis} yAxisRef={yAxisRef} xAxisRef={xAxisRef} boxPadding={boxPadding} padding={graphPadding} xAxisStyle={xAxisStyle} xAxisSVGProperties={xAxisSVGProperties} xAxisLabel={xAxisLabel}/>
                        {extraKids}
                    </BarChart>
                </View>
            </TouchableOpacity>
            {customXAxis}
        </View>
    </View>;
}


const ChartDetails = (props) => {
    const oldXPositions = useRef([]);
    const oldCustomMarginLeft = useRef(null);

    useEffect(()=>{
        oldXPositions.current = [];
        oldCustomMarginLeft.current = null;
    }, []);

    useEffect(() => {
        let values = [];
        let bandWidthMultiplier = 0.5;

        if(props.data.length > 0){
            if(props.horizontal == true){
                values = props.ticks;
                bandWidthMultiplier = 0;
            } else {
                if(props.data[0].data != undefined){
                    values = props.data[0].data.map((item, index) => props.accessor({ item, index }));
                } else {
                    values = props.data.map((item, index) => props.accessor({ item, index }));
                }
            }
        }

        const xPositions = _.map(values, (value, index) => {
            const pos = typeof value == "number" ? props.x(value) : props.x(index);
            return {value: value, index: index, pos: pos};
        });

        const customMarginLeft = props.yWidth + props.padding + props.chartMarginLeft + (props.isMobile? 10 : 0); //10 for margin between y axis and chart
        let shouldDraw = !_.isEqual(oldXPositions.current, xPositions);
        shouldDraw = shouldDraw || oldCustomMarginLeft.current != customMarginLeft;

        if(shouldDraw){
            oldXPositions.current = xPositions;
            oldCustomMarginLeft.current = customMarginLeft + 0;

            let viewWidth = 0;
            if(xPositions.length > 0){
                //(end+bandwidth)-(start-bandwidth) = (b+c)-(a-c) = = b-a+2c
                viewWidth = (xPositions[xPositions.length-1].pos - xPositions[0].pos) + 2*(0.5*props.bandwidth);
            }

            const extraSpaceBeforeFirstTick = 20;

            props.setCustomXAxis(
                <View style={{flexDirection: "column"}}>
                    <View style={{marginLeft: customMarginLeft-extraSpaceBeforeFirstTick}}>
                        <Svg style={props.xAxisStyle}>
                            <G>
                                {
                                    _.map(xPositions, (xPos, idx) => {
                                        const xPosition = xPos.pos;
                                        return <Text
                                            textAnchor={'middle'}
                                            alignmentBaseline={'hanging'}
                                            key={xPos.index + ":" + xPos.value}
                                            originX={xPosition}
                                            x={xPosition+(bandWidthMultiplier*props.bandwidth)+extraSpaceBeforeFirstTick}
                                            {...props.xAxisSVGProperties}
                                        >
                                            {props.formatLabel(xPos.value, xPos.index)}
                                        </Text>
                                    })
                                }
                            </G>
                        </Svg>
                    </View>
                    <View style={{height: 25, alignItems: "center", marginLeft: customMarginLeft, marginRight: props.padding}}>
                        {props.xAxisLabel != null? 
                            <Svg width={"100%"}>
                                <Text x={"50%"} y={"50%"} dominant-baseline="middle" text-anchor="middle" fontSize={12} fontFamily="Red Hat Display" fill="grey" fontWeight={500} letterSpacing={0.5}>
                                    {props.xAxisLabel}
                                </Text>
                            </Svg>
                        : null}
                    </View>
                </View>
            );
        }
    });
    
    return null;
}


export const CrazyLegend = ({currentDataPoint, data, legendChildren = null})=>{    
    const LegendFlexBlock = styled(FlexBlock)`
		justify-content: flex-start;
		align-items: flex-start;
		flex-direction: row;
	`;

    const ContentChunk = styled.View`
		flex-direction: row;
        align-items: center;
        flex-wrap: wrap;
        margin-right: 10px;
	`;

    if(legendChildren == null){
        const legendSeries = _.map(data, (d, idx)=>{
            let displayValue = "";
            let units = "MT";
            if(currentDataPoint != undefined){
                if(typeof(d) == "object" && d.data != undefined){
                    const correspondingDataPoint = _.find(d.data, {label: currentDataPoint.label});
                    if(correspondingDataPoint != undefined){
                        displayValue = Util.roundNice(correspondingDataPoint.value, 2);
                    }
                } else {
                    displayValue = Util.roundNice(currentDataPoint.value, 2);
                }

                if(displayValue.length > 0){
                    if(d.units != undefined){
                        units = d.units;
                    }
                    displayValue = displayValue + " " + units;
                }
            }

            let backgroundColour = null;
            if(d.svg != undefined){
                backgroundColour = d.svg.fill;
            } else if(d.colour != undefined) {
                backgroundColour = d.colour;
            }

            return <ContentChunk key={idx + ":" + displayValue}>
                <View style={{marginRight: 10, height: 20, width: 20, backgroundColor: backgroundColour}}/>
                <StrongText>{displayValue} {d.label}</StrongText>
            </ContentChunk>;
        });

        const legendHeading = <ContentChunk>
            <StrongText>
                {currentDataPoint == null ? null : 
                (currentDataPoint.legendLabel != undefined? 
                currentDataPoint.legendLabel :
                currentDataPoint.label)}
            </StrongText>
        </ContentChunk>;

        legendChildren = <LegendFlexBlock>
            {legendHeading}
            <ContentChunk>
                {legendSeries}
            </ContentChunk>
        </LegendFlexBlock>;
    } else {
        legendChildren = legendChildren(data, currentDataPoint);
    }

    if(legendChildren == null && false){
        return null;
    }

	return <View style={{flexDirection: "column", justifyContent: 'flex-start', flexGrow: 1, minHeight: 25, padding: 1}}>
        {legendChildren}
    </View>;
}

const MouseEvent = ({data, setCurrentDataPoint, mousePosition = [-1,-1], padding, bandwidth, x = undefined, y = undefined, yWidth, spacingOuter, spacingInner, boxPadding = 10, horizontal, customMouseEvent, isMobile = false})=>{
	let hoveredBand = undefined;
    const leftOffset = yWidth + padding + boxPadding;

    const assignHover = (band, idx) =>{
        const innerSpace = parseInt(idx) * spacingInner;
        //top left is [0,0] mouse position - 0 index is top

        if(horizontal == true){
            const startOfBand = y(idx) + 0 + innerSpace + spacingOuter + padding;
            
            if(startOfBand <= mousePosition[1] && mousePosition[1] <= startOfBand + bandwidth){
                hoveredBand = band;
            }
        } else {
            const startOfBand = x(idx) + (isMobile? 0 : leftOffset + innerSpace + spacingOuter);
            if(startOfBand <= mousePosition[0] && mousePosition[0] <= startOfBand + bandwidth){
                hoveredBand = band;
            }
        }
    }

    _.forEach(data, (d, idx)=>{ 
        if(typeof(d) == "object" && d.data != undefined){
            _.forEach(d.data, (dd, ddIdx)=>{
                if(hoveredBand == undefined){
                    assignHover(dd, ddIdx);
                }
            });
        } else {
            if(hoveredBand == undefined){
                assignHover(d, idx);
            }
        }
    });

    if(customMouseEvent != undefined){
        const res = customMouseEvent(hoveredBand);

        if(res){
            return;
        }
    }

	if (mousePosition[0] > 0 && mousePosition[1] > 0 ){
		setCurrentDataPoint(hoveredBand);
	}

	return null;
}

export default CustomBarChart;